import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAuth } from './AuthContext';
import { useSocket } from './SocketContext';
import { apiService } from '../services/api';
import PersonalTemplateSendProgress from '../components/PersonalTemplateSendProgress';
import TemplateSendProgress from '../components/TemplateSendProgress';

export type BulkSendType = 'personal' | 'cloud';

export interface ActiveBulkSession {
  sessionId: string;
  type: BulkSendType;
  templateName: string;
  status: string;
  totalRecords: number;
  successCount: number;
  failedCount: number;
  cancelledCount: number;
  nextSendAt?: string | null;
  pausedReason?: string | null;
}

interface TrackSessionInput {
  sessionId: string;
  type: BulkSendType;
  templateName: string;
  totalRecords: number;
  status?: string;
}

interface ActiveBulkSendContextValue {
  activeSessions: ActiveBulkSession[];
  progressSessionId: string | null;
  trackSession: (session: TrackSessionInput) => void;
  openProgress: (sessionId: string, fallback?: ActiveBulkSession) => void;
  closeProgress: () => void;
  refreshActiveSessions: () => Promise<void>;
  removeSession: (sessionId: string) => void;
}

const ActiveBulkSendContext = createContext<ActiveBulkSendContextValue | null>(null);

const TERMINAL_STATUSES = new Set(['completed', 'cancelled']);

const mapPersonalSession = (session: any): ActiveBulkSession => ({
  sessionId: session.sessionId,
  type: 'personal',
  templateName: session.templateName,
  status: session.status,
  totalRecords: session.totalRecords || 0,
  successCount: session.successCount || 0,
  failedCount: session.failedCount || 0,
  cancelledCount: session.cancelledCount || 0,
  nextSendAt: session.nextSendAt || null,
  pausedReason: session.pausedReason || null,
});

const mapCloudSession = (session: any): ActiveBulkSession => ({
  sessionId: session.sessionId,
  type: 'cloud',
  templateName: session.templateName,
  status: session.status,
  totalRecords: session.totalRecords || 0,
  successCount: session.successCount || 0,
  failedCount: session.failedCount || 0,
  cancelledCount: session.cancelledCount || 0,
});

const mergeSessions = (
  personalSessions: ActiveBulkSession[],
  cloudSessions: ActiveBulkSession[],
) => {
  const merged = new Map<string, ActiveBulkSession>();
  [...personalSessions, ...cloudSessions].forEach((session) => {
    merged.set(session.sessionId, session);
  });
  return Array.from(merged.values());
};

export const ActiveBulkSendProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { state: authState } = useAuth();
  const { socket } = useSocket();
  const [activeSessions, setActiveSessions] = useState<ActiveBulkSession[]>([]);
  const [progressSession, setProgressSession] = useState<ActiveBulkSession | null>(null);

  const removeSession = useCallback((sessionId: string) => {
    setActiveSessions((prev) => prev.filter((s) => s.sessionId !== sessionId));
  }, []);

  const updateSession = useCallback(
    (sessionId: string, updater: (session: ActiveBulkSession) => ActiveBulkSession) => {
      setActiveSessions((prev) =>
        prev.map((session) =>
          session.sessionId === sessionId ? updater(session) : session,
        ),
      );
      setProgressSession((current) =>
        current?.sessionId === sessionId ? updater(current) : current,
      );
    },
    [],
  );

  const refreshActiveSessions = useCallback(async () => {
    if (!authState.isAuthenticated) {
      setActiveSessions([]);
      return;
    }

    try {
      const [personalResponse, cloudResponse] = await Promise.all([
        apiService.getActivePersonalTemplateSessions(),
        apiService.getActiveTemplateSendSessions(),
      ]);

      const personalSessions = (personalResponse.data.data?.sessions || []).map(
        mapPersonalSession,
      );
      const cloudSessions = (cloudResponse.data.data?.sessions || []).map(mapCloudSession);

      setActiveSessions(mergeSessions(personalSessions, cloudSessions));
    } catch (error) {
      console.error('Failed to refresh active bulk send sessions:', error);
    }
  }, [authState.isAuthenticated]);

  const trackSession = useCallback((session: TrackSessionInput) => {
    const nextSession: ActiveBulkSession = {
      sessionId: session.sessionId,
      type: session.type,
      templateName: session.templateName,
      totalRecords: session.totalRecords,
      status: session.status || 'in_progress',
      successCount: 0,
      failedCount: 0,
      cancelledCount: 0,
    };

    setActiveSessions((prev) => {
      const existing = prev.find((item) => item.sessionId === session.sessionId);
      if (existing) {
        return prev.map((item) =>
          item.sessionId === session.sessionId ? { ...existing, ...nextSession } : item,
        );
      }
      return [nextSession, ...prev];
    });
    setProgressSession(nextSession);
  }, []);

  const openProgress = useCallback((sessionId: string, fallback?: ActiveBulkSession) => {
    setActiveSessions((sessions) => {
      const session = sessions.find((item) => item.sessionId === sessionId) || fallback;
      if (session) {
        setProgressSession(session);
        if (fallback && !sessions.some((item) => item.sessionId === sessionId)) {
          return [fallback, ...sessions];
        }
      }
      return sessions;
    });
  }, []);

  const closeProgress = useCallback(() => {
    setProgressSession(null);
  }, []);

  useEffect(() => {
    refreshActiveSessions();
  }, [refreshActiveSessions]);

  useEffect(() => {
    if (!authState.isAuthenticated || activeSessions.length === 0) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      refreshActiveSessions();
    }, 30000);

    return () => window.clearInterval(intervalId);
  }, [authState.isAuthenticated, activeSessions.length, refreshActiveSessions]);

  useEffect(() => {
    if (!progressSession) return;

    const updated = activeSessions.find(
      (session) => session.sessionId === progressSession.sessionId,
    );
    if (updated) {
      setProgressSession(updated);
    }
  }, [activeSessions, progressSession]);

  useEffect(() => {
    if (!socket) return undefined;

    const handlePersonalProgress = (data: any) => {
      if (!data?.sessionId) return;

      updateSession(data.sessionId, (session) => ({
        ...session,
        successCount:
          data.status === 'success'
            ? session.successCount + 1
            : session.successCount,
        failedCount:
          data.status === 'failed' ? session.failedCount + 1 : session.failedCount,
      }));
    };

    const handlePersonalComplete = (data: any) => {
      if (!data?.sessionId) return;

      updateSession(data.sessionId, (session) => ({
        ...session,
        status: 'completed',
        successCount: data.successCount ?? session.successCount,
        failedCount: data.failedCount ?? session.failedCount,
        cancelledCount: data.cancelledCount ?? session.cancelledCount,
      }));
      removeSession(data.sessionId);
    };

    const handlePersonalCancelled = (data: any) => {
      if (!data?.sessionId) return;

      updateSession(data.sessionId, (session) => ({
        ...session,
        status: 'cancelled',
      }));
      removeSession(data.sessionId);
    };

    const handlePersonalPaused = (data: any) => {
      if (!data?.sessionId) return;
      updateSession(data.sessionId, (session) => ({
        ...session,
        status: 'paused',
        pausedReason: data.pausedReason || session.pausedReason || null,
        nextSendAt: data.pausedUntil || data.nextSendAt || session.nextSendAt || null,
      }));
    };

    const handlePersonalScheduled = (data: any) => {
      if (!data?.sessionId) return;
      updateSession(data.sessionId, (session) => ({
        ...session,
        status: 'scheduled',
        pausedReason: data.pausedReason || session.pausedReason || null,
        nextSendAt: data.nextSendAt || session.nextSendAt || null,
      }));
    };

    const handlePersonalResumed = (data: any) => {
      if (!data?.sessionId) return;
      updateSession(data.sessionId, (session) => ({
        ...session,
        status: data.status || 'in_progress',
        pausedReason: null,
        nextSendAt: null,
      }));
    };

    const handleCloudProgress = (data: any) => {
      if (!data?.sessionId) return;

      updateSession(data.sessionId, (session) => ({
        ...session,
        successCount:
          data.status === 'success'
            ? session.successCount + 1
            : session.successCount,
        failedCount:
          data.status === 'failed' ? session.failedCount + 1 : session.failedCount,
      }));
    };

    const handleCloudComplete = (data: any) => {
      if (!data?.sessionId) return;

      updateSession(data.sessionId, (session) => ({
        ...session,
        status: 'completed',
        successCount: data.successCount ?? session.successCount,
        failedCount: data.failedCount ?? session.failedCount,
        cancelledCount: data.cancelledCount ?? session.cancelledCount,
      }));
      removeSession(data.sessionId);
    };

    const handleCloudCancelled = (data: any) => {
      if (!data?.sessionId) return;

      updateSession(data.sessionId, (session) => ({
        ...session,
        status: 'cancelled',
      }));
      removeSession(data.sessionId);
    };

    socket.on('personal_template_send_progress', handlePersonalProgress);
    socket.on('personal_template_send_complete', handlePersonalComplete);
    socket.on('personal_template_send_cancelled', handlePersonalCancelled);
    socket.on('personal_template_send_paused', handlePersonalPaused);
    socket.on('personal_template_send_scheduled', handlePersonalScheduled);
    socket.on('personal_template_send_resumed', handlePersonalResumed);
    socket.on('template_send_progress', handleCloudProgress);
    socket.on('template_send_complete', handleCloudComplete);
    socket.on('template_send_cancelled', handleCloudCancelled);

    return () => {
      socket.off('personal_template_send_progress', handlePersonalProgress);
      socket.off('personal_template_send_complete', handlePersonalComplete);
      socket.off('personal_template_send_cancelled', handlePersonalCancelled);
      socket.off('personal_template_send_paused', handlePersonalPaused);
      socket.off('personal_template_send_scheduled', handlePersonalScheduled);
      socket.off('personal_template_send_resumed', handlePersonalResumed);
      socket.off('template_send_progress', handleCloudProgress);
      socket.off('template_send_complete', handleCloudComplete);
      socket.off('template_send_cancelled', handleCloudCancelled);
    };
  }, [socket, removeSession, updateSession]);

  const handleSessionComplete = useCallback(() => {
    refreshActiveSessions();
  }, [refreshActiveSessions]);

  const contextValue = useMemo(
    () => ({
      activeSessions,
      progressSessionId: progressSession?.sessionId || null,
      trackSession,
      openProgress,
      closeProgress,
      refreshActiveSessions,
      removeSession,
    }),
    [
      activeSessions,
      progressSession,
      trackSession,
      openProgress,
      closeProgress,
      refreshActiveSessions,
      removeSession,
    ],
  );

  return (
    <ActiveBulkSendContext.Provider value={contextValue}>
      {children}

      {progressSession?.type === 'personal' && (
        <PersonalTemplateSendProgress
          open={Boolean(progressSession)}
          onClose={closeProgress}
          sessionId={progressSession.sessionId}
          totalRecords={progressSession.totalRecords}
          templateName={progressSession.templateName}
          onComplete={handleSessionComplete}
        />
      )}

      {progressSession?.type === 'cloud' && (
        <TemplateSendProgress
          open={Boolean(progressSession)}
          onClose={closeProgress}
          sessionId={progressSession.sessionId}
          totalRecords={progressSession.totalRecords}
          templateName={progressSession.templateName}
          onComplete={handleSessionComplete}
        />
      )}
    </ActiveBulkSendContext.Provider>
  );
};

export const useActiveBulkSend = (): ActiveBulkSendContextValue => {
  const context = useContext(ActiveBulkSendContext);
  if (!context) {
    throw new Error('useActiveBulkSend must be used within ActiveBulkSendProvider');
  }
  return context;
};

export const isActiveBulkSessionStatus = (status: string) => !TERMINAL_STATUSES.has(status);
